import React, { useState, useEffect, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { EmptyState, LoadingState, MetricCard, PageHeader, TableToolbar } from './AdminUI';
import { alertError, alertSuccess, confirmDelete } from '../../utils/swal';
import { API_BASE } from '../../config/api';

const BlogManagement = () => {
    const { apiCall } = useAuth();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        author: 'StatsElite Team',
        category: 'MERL',
        tags: [],
        image: 'images/blog/default.jpg',
        excerpt: '',
        content: '',
        published: true,
        featured: false
    });
    const [tagInput, setTagInput] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [tableSearch, setTableSearch] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchBlogs();
    }, []);

    const fetchBlogs = async () => {
        try {
            const response = await apiCall('/blog');
            const data = await response.json();
            // Ensure we always set an array
            const blogs = Array.isArray(data.blogs) ? data.blogs :
                         Array.isArray(data) ? data : [];
            setBlogs(blogs);
        } catch (error) {
            console.error('Error fetching blogs:', error);
            setBlogs([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingBlog ? `/blog/${editingBlog.id}` : '/blog';
            const method = editingBlog ? 'PUT' : 'POST';

            let body;
            if (imageFile) {
                const fd = new FormData();
                fd.append('title', formData.title);
                fd.append('slug', formData.slug);
                fd.append('author', formData.author);
                fd.append('category', formData.category);
                fd.append('tags', JSON.stringify(formData.tags));
                fd.append('excerpt', formData.excerpt);
                fd.append('content', formData.content);
                fd.append('published', formData.published);
                fd.append('featured', formData.featured);
                fd.append('image', imageFile);
                body = fd;
            } else {
                body = JSON.stringify(formData);
            }

            const response = await apiCall(url, { method, body });
            const result = await response.json();

            if (response.ok) {
                await fetchBlogs();
                handleCloseModal();
                alertSuccess('Saved', 'Blog post saved successfully.');
            } else {
                alertError('Error', result.message || 'Error saving blog post');
            }
        } catch (error) {
            console.error('Error saving blog:', error);
            alertError('Error', 'Error saving blog post');
        }
    };

    const handleEdit = (blog) => {
        setEditingBlog(blog);
        setFormData({
            title: blog.title,
            slug: blog.slug,
            author: blog.author,
            category: blog.category,
            tags: Array.isArray(blog.tags) ? blog.tags : [],
            image: blog.image,
            excerpt: blog.excerpt,
            content: blog.content,
            published: blog.published,
            featured: blog.featured
        });
        setImageFile(null);
        setImagePreview(null);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const { isConfirmed } = await confirmDelete({ title: 'Delete blog post?', text: 'Are you sure? This cannot be undone.' });
        if (!isConfirmed) return;

        try {
            const response = await apiCall(`/blog/${id}`, { method: 'DELETE' });
            if (response.ok) {
                await fetchBlogs();
                alertSuccess('Deleted', 'Blog post deleted.');
            } else {
                alertError('Error', 'Error deleting blog post');
            }
        } catch (error) {
            console.error('Error deleting blog:', error);
            alertError('Error', 'Error deleting blog post');
        }
    };

    const handleTogglePublish = async (blog) => {
        try {
            const response = await apiCall(`/blog/${blog.id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...blog, published: !blog.published })
            });
            if (response.ok) {
                await fetchBlogs();
            }
        } catch (error) {
            console.error('Error toggling publish status:', error);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingBlog(null);
        setFormData({
            title: '',
            slug: '',
            author: 'StatsElites Team',
            category: 'MERL',
            tags: [],
            image: 'images/blog/default.jpg',
            excerpt: '',
            content: '',
            published: true,
            featured: false
        });
        setTagInput('');
        setImageFile(null);
        setImagePreview(null);
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        } else {
            setImageFile(null);
            setImagePreview(null);
        }
    };

    const getPreviewImageUrl = () => {
        if (imagePreview) return imagePreview;
        if (!formData.image) return null;
        if (formData.image.startsWith('uploads/')) {
            const base = API_BASE.replace(/\/api\/?$/, '');
            return `${base}/${formData.image}`;
        }
        return formData.image.startsWith('http') ? formData.image : null;
    };

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        setFormData({ ...formData, title, slug: generateSlug(title) });
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
    };

    const categories = [
        'MERL', 'Statistics', 'Data Science', 'Capacity Building',
        'Case Studies', 'Research', 'Evaluation', 'Data',
        'Training', 'Consulting', 'Analysis', 'Impact'
    ];

    const blogStats = useMemo(() => ({
        total: blogs.length,
        published: blogs.filter((blog) => blog.published).length,
        drafts: blogs.filter((blog) => !blog.published).length,
        featured: blogs.filter((blog) => blog.featured).length
    }), [blogs]);

    const filteredBlogs = useMemo(() => {
        const query = tableSearch.trim().toLowerCase();
        if (!query) return blogs;

        return blogs.filter((blog) => [
            blog.title,
            blog.slug,
            blog.author,
            blog.category,
            blog.published ? 'published' : 'draft',
            blog.featured ? 'featured' : ''
        ].some((value) => String(value || '').toLowerCase().includes(query)));
    }, [blogs, tableSearch]);

    const blogColumns = [
        {
            name: 'Title',
            selector: (row) => row.title,
            sortable: true,
            cell: (row) => (
                <div className="admin-list-primary">
                    <div className="admin-list-title">{row.title}</div>
                    <div className="admin-list-subtitle">{row.slug}</div>
                </div>
            ),
            grow: 2
        },
        {
            name: 'Category',
            selector: (row) => row.category,
            sortable: true,
            cell: (row) => (
                <span className="admin-pill admin-pill-brand">
                    {row.category}
                </span>
            )
        },
        { name: 'Author', selector: (row) => row.author, sortable: true },
        {
            name: 'Status',
            cell: (row) => (
                <span className="admin-row-actions">
                    <button
                        type="button"
                        onClick={() => handleTogglePublish(row)}
                        className={`admin-pill-button ${row.published ? 'is-published' : 'is-draft'}`}
                    >
                        {row.published ? 'Published' : 'Draft'}
                    </button>
                    {row.featured && (
                        <span className="admin-pill admin-pill-warm">
                            Featured
                        </span>
                    )}
                </span>
            ),
            width: '160px'
        },
        {
            name: 'Actions',
            cell: (row) => (
                <span className="admin-row-actions">
                    <button
                        type="button"
                        onClick={() => handleEdit(row)}
                        className="btn btn-primary btn-icon"
                        title="Edit"
                    >
                        <i className="fa fa-edit"></i>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleDelete(row.id)}
                        className="btn btn-danger btn-icon"
                        title="Delete"
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                </span>
            ),
            width: '120px',
            ignoreRowClick: true
        }
    ];

    const tableCustomStyles = {
        headCells: {
            style: {
                padding: '12px 16px',
                fontWeight: '600',
                fontSize: '14px',
                color: '#4a5568',
                backgroundColor: '#f7fafc',
                borderBottom: '2px solid #e2e8f0'
            }
        },
        cells: {
            style: {
                padding: '12px 16px',
                fontSize: '14px',
                color: '#4a5568'
            }
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <LoadingState label="Loading blog posts..." />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-page">
                <PageHeader
                    title="Blog Posts"
                    subtitle="Manage your blog content"
                    action={
                    <button
                        onClick={() => setShowModal(true)}
                        className="btn btn-primary"
                    >
                        <i className="fa fa-plus"></i>
                        New Blog Post
                    </button>
                    }
                />

                <div className="analytics-metrics-grid">
                    <MetricCard label="Total Posts" value={blogStats.total} icon="fa fa-pencil-square-o" accent="#62B6E8" detail="All blog content" />
                    <MetricCard label="Published" value={blogStats.published} icon="fa fa-check-circle" accent="#62B6E8" detail="Visible on website" />
                    <MetricCard label="Drafts" value={blogStats.drafts} icon="fa fa-file-o" accent="#62B6E8" detail="Still hidden" />
                    <MetricCard label="Featured" value={blogStats.featured} icon="fa fa-star" accent="#62B6E8" detail="Promoted posts" />
                </div>

                <div className="card admin-data-card">
                    <TableToolbar
                        search={tableSearch}
                        onSearchChange={setTableSearch}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={setRowsPerPage}
                        placeholder="Search blog posts..."
                    />
                    <DataTable
                        columns={blogColumns}
                        data={filteredBlogs}
                        customStyles={tableCustomStyles}
                        pagination
                        paginationPerPage={rowsPerPage}
                        paginationRowsPerPageOptions={[10, 20, 30]}
                        paginationComponentOptions={{ noRowsPerPage: true }}
                        progressPending={loading}
                        progressComponent={<div style={{ padding: '24px', color: '#718096' }}>Loading...</div>}
                        noDataComponent={
                            <EmptyState
                                icon="fa fa-pencil-square-o"
                                title="No blog posts yet"
                                text="Create your first blog post to publish content."
                            />
                        }
                        striped
                        dense
                    />
                </div>

                {/* Modal */}
                {showModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '0',
                            maxWidth: '800px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            padding: '32px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                                    {editingBlog ? 'Edit Blog Post' : 'New Blog Post'}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#718096' }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={handleTitleChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px'
                                        }}
                                        placeholder="Enter blog title"
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Slug *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px'
                                        }}
                                        placeholder="url-friendly-slug"
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Author *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.author}
                                            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Category *
                                        </label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        >
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Featured image
                                    </label>
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.gif,.webp"
                                        onChange={handleImageChange}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px'
                                        }}
                                    />
                                    {getPreviewImageUrl() && (
                                        <div style={{ marginTop: '12px' }}>
                                            <img
                                                src={getPreviewImageUrl()}
                                                alt="Preview"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '200px',
                                                    objectFit: 'cover',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '0'
                                                }}
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Tags
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                        <input
                                            type="text"
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                                            style={{
                                                flex: 1,
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                            placeholder="Add tag and press Enter"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddTag}
                                            style={{
                                                padding: '10px 20px',
                                                background: '#62B6E8',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Add
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {formData.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                style={{
                                                    background: '#e3f2fd',
                                                    color: '#1976d2',
                                                    padding: '4px 12px',
                                                    borderRadius: '0',
                                                    fontSize: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTag(tag)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1976d2' }}
                                                >
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Excerpt *
                                    </label>
                                    <textarea
                                        value={formData.excerpt}
                                        onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                                        required
                                        rows={3}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                        placeholder="Brief summary of the blog post"
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Content *
                                    </label>
                                    <textarea
                                        value={formData.content}
                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        required
                                        rows={10}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px',
                                            resize: 'vertical',
                                            fontFamily: 'monospace'
                                        }}
                                        placeholder="Full blog content (supports HTML)"
                                    />
                                </div>

                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.published}
                                            onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <span style={{ fontWeight: '500', color: '#2d3748' }}>Publish immediately</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.featured}
                                            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <span style={{ fontWeight: '500', color: '#2d3748' }}>Featured post</span>
                                    </label>
                                </div>

                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        style={{
                                            padding: '12px 24px',
                                            background: '#e2e8f0',
                                            color: '#4a5568',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px 24px',
                                            background: '#62B6E8',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {editingBlog ? 'Update' : 'Create'} Blog Post
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default BlogManagement;
